<template>
  <div
    class="relative w-full"
    @keydown.esc.stop.prevent="close"
  >
    <label
      v-if="label"
      :for="inputId"
      class="text-alert-closed-text mb-1 block text-xs font-semibold"
    >
      {{ label }}
    </label>

    <div
      :class="[
        'group flex items-center rounded border transition-colors',
        isFocused ? 'border-neutral-ring' : 'borderGray',
        containerBgClass,
      ]"
      @mousedown.stop
    >
      <input
        :id="inputId"
        ref="inputRef"
        v-model="query"
        :placeholder="placeholder"
        autocomplete="off"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isOpen"
        :aria-controls="listId"
        class="h-10 w-full rounded border-none bg-transparent py-2 pl-1 pr-3 text-sm font-normal placeholder-neutral-subtlest outline-none"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
        @keydown.tab="close"
      />
      <div
        class="flex h-full items-center bg-transparent pr-2 transition-transform duration-200"
        @click="isOpen = !isOpen"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.85865 10.2728C5.66065 10.0748 5.56164 9.97579 5.52455 9.86163C5.49192 9.76121 5.49192 9.65303 5.52455 9.55261C5.56164 9.43845 5.66065 9.33944 5.85865 9.14144L6.1415 8.85859C6.3395 8.66058 6.43851 8.56158 6.55267 8.52449C6.6531 8.49186 6.76127 8.49186 6.86169 8.52449C6.97585 8.56158 7.07486 8.66058 7.27287 8.85859L11.4344 13.0201C11.6324 13.2181 11.7314 13.3171 11.8456 13.3542C11.946 13.3869 12.0542 13.3869 12.1546 13.3542C12.2687 13.3171 12.3678 13.2181 12.5658 13.0201L16.7273 8.85859C16.9253 8.66058 17.0243 8.56158 17.1385 8.52449C17.2389 8.49186 17.3471 8.49186 17.4475 8.52449C17.5616 8.56158 17.6606 8.66058 17.8587 8.85859L18.1415 9.14144C18.3395 9.33944 18.4385 9.43845 18.4756 9.55261C18.5082 9.65303 18.5082 9.76121 18.4756 9.86163C18.4385 9.97579 18.3395 10.0748 18.1415 10.2728L12.5658 15.8485C12.3678 16.0466 12.2687 16.1456 12.1546 16.1826C12.0542 16.2153 11.946 16.2153 11.8456 16.1826C11.7314 16.1456 11.6324 16.0466 11.4344 15.8485L5.85865 10.2728Z"
            fill="#6C7C9D"
          />
        </svg>
      </div>
    </div>
    <!-- Dropdown -->
    <ul
      v-if="isOpen"
      :id="listId"
      class="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded bg-white shadow-md"
      :class="{
        'borderGray border':
          hasResults || (query.length >= minChars && allowCreate),
      }"
      role="listbox"
    >
      <template v-if="hasResults">
        <li
          v-for="(option, index) in filteredOptions"
          :key="optionKey(option, index)"
          :class="[
            'text-info-bold-text hover:bg-alert-closed-bg cursor-pointer px-4 py-2 text-sm font-normal',
            highlightedIndex === index ? 'bg-alert-closed-bg' : '',
          ]"
          role="option"
          :aria-selected="highlightedIndex === index"
          @mousedown.prevent="selectOption(option)"
        >
          {{ optionText(option) }}
        </li>
      </template>

      <template v-else-if="query.length >= minChars && allowCreate">
        <li class="px-4 py-3 text-center text-sm font-normal text-steel-400">
          No matches found for <span class="font-normal">{{ query }}</span>
        </li>
        <li
          class="cursor-pointer px-4 pb-4 text-center text-sm font-medium text-sun-700 hover:text-sun-800 focus:text-sun-900"
          role="button"
          @mousedown.prevent="requestCreate"
        >
          + {{ createLabel }}
        </li>
      </template>
    </ul>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  defineExpose,
} from 'vue';

const props = defineProps({
  label: String,
  placeholder: String,
  options: { type: Array, default: () => [] },
  modelValue: [String, Number, Object, null],
  minChars: { type: Number, default: 1 },
  allowCreate: { type: Boolean, default: true },
  createLabel: { type: String, default: 'Add Name' },
  labelKey: { type: String, default: 'description' },
  valueKey: { type: String, default: 'value' },
  filterFn: { type: Function, default: null },
});

const emit = defineEmits([
  'update:modelValue',
  'selected',
  'request-create',
  'opened',
  'closed',
]);

/* ---------- STATE ---------- */
const query = ref('');
const isFocused = ref(false);
const isOpen = ref(false);
const highlightedIndex = ref(-1);
const inputRef = ref(null);
const inputId = `ac-${Math.random().toString(36).slice(2)}`;
const listId = `list-${inputId}`;
const localOptions = ref([]);

watch(
  () => props.options,
  (v) => (localOptions.value = v || []),
  { immediate: true }
);
watch(query, (newVal) => {
  emit('selected', newVal);
});

watch(
  () => props.modelValue,
  (nv) => {
    const match = localOptions.value.find((o) => getValue(o) === nv);
    query.value = match ? optionText(match) : (nv ?? '');
  },
  { immediate: true }
);

/* ---------- COMPUTEDS ---------- */
const filteredOptions = computed(() => {
  const q = normalize(query.value);
  if (!q) return localOptions.value;
  if (props.filterFn) return props.filterFn(localOptions.value, query.value);
  return localOptions.value.filter((o) => normalize(optionText(o)).includes(q));
});
const hasResults = computed(() => filteredOptions.value.length > 0);

const containerBgClass = computed(() => 'bg-steel-100 hover:bg-steel-200');

/* ---------- HELPERS ---------- */
function getValue(option) {
  return typeof option === 'object' ? option[props.valueKey] : option;
}
function optionText(option) {
  return typeof option === 'object' ? option[props.labelKey] : String(option);
}
function optionKey(option, i) {
  const val = getValue(option);
  return val ?? i;
}
const normalize = (s) =>
  s
    ?.toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '') ?? '';

/* ---------- UI EVENTS ---------- */
function onFocus() {
  isFocused.value = true;
  open();
}
function onBlur() {
  isFocused.value = false;
  setTimeout(close, 100);
}

function onInput() {
  if (query.value.length >= props.minChars) open();
  else close();
  highlightedIndex.value = hasResults.value ? 0 : -1;
}

function selectOption(option) {
  query.value = optionText(option);
  emit('update:modelValue', getValue(option));
  emit('selected', option);
  close();
  highlightedIndex.value = -1;
}

function requestCreate() {
  emit('selected', query.value);
  close();
}

function onArrowDown() {
  if (!isOpen.value) return open();
  highlightedIndex.value =
    (highlightedIndex.value + 1) % filteredOptions.value.length;
}
function onArrowUp() {
  if (!isOpen.value) return;
  highlightedIndex.value =
    (highlightedIndex.value - 1 + filteredOptions.value.length) %
    filteredOptions.value.length;
}
function onEnter() {
  if (!isOpen.value) return;
  if (hasResults.value && highlightedIndex.value >= 0) {
    selectOption(filteredOptions.value[highlightedIndex.value]);
  } else if (!hasResults.value && props.allowCreate) {
    requestCreate();
  }
}

function open() {
  if (!isOpen.value) {
    isOpen.value = true;
    emit('opened');
  }
}
function close() {
  if (isOpen.value) {
    isOpen.value = false;
    highlightedIndex.value = -1;
    emit('closed');
  }
}

/* ---------- Exposed API ---------- */
function addOption(option, select = true) {
  localOptions.value.push(option);
  if (select) {
    query.value = optionText(option);
    emit('update:modelValue', getValue(option));
    emit('selected', option);
  }
}
defineExpose({ addOption, focus: () => inputRef.value?.focus() });

/* ---------- Click outside ---------- */
function handleClick(e) {
  if (!inputRef.value) return;
  if (!inputRef.value.closest('.relative.w-full')?.contains(e.target)) close();
}
onMounted(() => document.addEventListener('mousedown', handleClick));
onBeforeUnmount(() => document.removeEventListener('mousedown', handleClick));
</script>

<style scoped>
input:focus {
  outline: none !important;
  box-shadow: none !important;
}
.borderGray {
  border-color: rgba(31, 36, 46, 0.14);
}
</style>
